/* eslint-disable max-lines */
import { Fraction } from 'bi-fraction';
import { id, Interface, zeroPadValue } from 'ethers';
import { L1Signer, Provider } from 'zksync-ethers';
import { IL1SharedBridge__factory } from 'zksync-ethers/build-esm/typechain';
import { L2_BASE_TOKEN_ADDRESS } from 'zksync-ethers/build-esm/utils';
import { Bridge as BridgeAbi, ERC20 as ERC20Abi, L2BaseToken as L2BaseTokenAbi, } from './abis';
import { ZKCRO_DECIMALS } from './config/constants';
import { ChainLayer, } from './types';
import { calculateAmountBigInt, executeTx, getContract, getProvider, } from './utils';
/**
 * Bridge class that allows to deposit and withdraw tokens between L1 and L2.
 */
export class Bridge {
    /**
     * Constructor
     *
     * @param l2providerOptions options for the L2 (zkEVM) provider
     */
    constructor(l1providerOptions, l2providerOptions, contractAddressMap) {
        this.l1providerOptions = l1providerOptions;
        this.l2providerOptions = l2providerOptions;
        this.contractAddressMap = contractAddressMap;
        this.getWithdrawTransactions = async (receiverAddress, signer) => {
            const baseTokenWithdrawPromises = this.getBaseTokenWithdrawTransactions(receiverAddress, signer);
            const provider = this.getProviderOnL2();
            const eventSignature = 'WithdrawalInitiated(address,address,address,uint256)';
            const iface = new Interface(JSON.stringify(withdrawalEventABI));
            const eventSelector = id(eventSignature);
            const [logs, finalizedBlock] = await Promise.all([
                provider.getLogs({
                    address: this.contractAddressMap.Bridge,
                    topics: [eventSelector, null, zeroPadValue(receiverAddress, 32), null],
                    fromBlock: '0x0',
                    toBlock: 'latest',
                }),
                this.getProviderOnL2().getBlock('finalized'),
            ]);
            const parsedLogs = logs.map((l) => iface.parseLog(l));
            const parsedLogsWithTxHash = parsedLogs
                .map((parsedLog, i) => parsedLog
                ? {
                    ...parsedLog,
                    transactionHash: logs[i].transactionHash,
                    blockNumber: logs[i].blockNumber,
                }
                : null)
                .filter((log) => log !== null);
            const l1Signer = this.getL1Signer(signer);
            const tokenAddresses = [
                ...new Set(
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                parsedLogsWithTxHash.map((parsedLog) => parsedLog.args[2])),
            ];
            const tokenContracts = tokenAddresses.map((tokenAddress) => getContract(tokenAddress, ERC20Abi, this.getProviderOnL2()));
            const isWithdrawalFinalizedPromises = parsedLogsWithTxHash.map((parsedLog) => this.checkIsFinalized(l1Signer, parsedLog, finalizedBlock.number));
            const decimalsPromises = tokenContracts.map((tokenContract) => tokenContract.decimals().then(Number));
            const symbolPromises = tokenContracts.map((tokenContract) => this.getSymbol(tokenContract));
            const [isWithdrawalFinalized, decimalsArray, symbolsArray] = await Promise.all([
                Promise.all(isWithdrawalFinalizedPromises),
                Promise.all(decimalsPromises),
                Promise.all(symbolPromises),
            ]);
            const tokenAddressDecimalsPairs = tokenAddresses.map((tokenAddress, index) => [tokenAddress, decimalsArray[index]]);
            const tokenAddressSymbolPairs = tokenAddresses.map((tokenAddress, index) => [tokenAddress, symbolsArray[index]]);
            const tokenDecimalsMap = new Map(tokenAddressDecimalsPairs);
            const symbolMap = new Map(tokenAddressSymbolPairs);
            const result = parsedLogsWithTxHash.map((parsedLog, i) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const l2TokenAddress = parsedLog.args[2];
                return {
                    l2TokenAddress,
                    amount: new Fraction(parsedLog.args[3]).shr(
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    tokenDecimalsMap.get(l2TokenAddress)),
                    transactionHash: parsedLog.transactionHash,
                    finalized: isWithdrawalFinalized[i],
                    readyToFinalize: finalizedBlock.number >= parsedLog.blockNumber,
                    name: symbolMap.get(l2TokenAddress),
                    blockNumber: parsedLog.blockNumber,
                };
            });
            const baseTokenWithdrawResults = await baseTokenWithdrawPromises;
            const combinedResults = [...result, ...baseTokenWithdrawResults];
            combinedResults.sort((a, b) => {
                return a.blockNumber - b.blockNumber;
            });
            return combinedResults;
        };
        this.getBaseTokenWithdrawTransactions = async (receiverAddress, signer) => {
            const provider = this.getProviderOnL2();
            const eventSignature = 'Withdrawal(address,address,uint256)';
            const iface = new Interface(JSON.stringify(baseTokenWithdrawalEventABI));
            const eventSelector = id(eventSignature);
            const [logs, finalizedBlock] = await Promise.all([
                provider.getLogs({
                    address: L2_BASE_TOKEN_ADDRESS,
                    topics: [eventSelector, null, zeroPadValue(receiverAddress, 32)],
                    fromBlock: '0x0',
                    toBlock: 'latest',
                }),
                this.getProviderOnL2().getBlock('finalized'),
            ]);
            const parsedLogs = logs.map((l) => iface.parseLog(l));
            const parsedLogsWithTxHash = parsedLogs
                .map((parsedLog, i) => parsedLog
                ? {
                    ...parsedLog,
                    transactionHash: logs[i].transactionHash,
                    blockNumber: logs[i].blockNumber,
                }
                : null)
                .filter((log) => log !== null);
            const l1Signer = this.getL1Signer(signer);
            const isWithdrawalFinalizedPromises = parsedLogsWithTxHash.map((parsedLog) => this.checkIsFinalized(l1Signer, parsedLog, finalizedBlock.number));
            const [isWithdrawalFinalized] = await Promise.all([
                Promise.all(isWithdrawalFinalizedPromises),
            ]);
            const result = parsedLogsWithTxHash.map((parsedLog, i) => {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                const l2TokenAddress = L2_BASE_TOKEN_ADDRESS;
                return {
                    l2TokenAddress,
                    amount: new Fraction(parsedLog.args[2]).shr(ZKCRO_DECIMALS),
                    transactionHash: parsedLog.transactionHash,
                    finalized: isWithdrawalFinalized[i],
                    readyToFinalize: finalizedBlock.number >= parsedLog.blockNumber,
                    blockNumber: parsedLog.blockNumber,
                    name: 'ZkCRO',
                };
            });
            return result;
        };
        this.finalizeWithdrawal = async (signer, transactionHash) => {
            const tx = this.getL1Signer(signer).finalizeWithdrawal(transactionHash);
            return executeTx(tx);
        };
        /**
         * @returns what `symbol` function gives us, but this is not on ETH, so for ETH contract just return "ETH"
         */
        this.getSymbol = async (tokenContract) => {
            if ((await tokenContract.getAddress()) === this.contractAddressMap.ETH)
                return 'ETH';
            return tokenContract.symbol();
        };
        /**
         *  Withdraw tokens from L2 to L1.
         *
         * @param rpcSigner signer on L2
         * @param to address to receive tokens on L1
         * @param withdrawTokenAddress address on L2 of token to withdraw
         * @param amount amount to withdraw
         * @returns a TransactionResponse promise
         */
        this.withdrawToken = async (rpcSigner, to, withdrawTokenAddress, amount) => {
            const tokenContract = getContract(withdrawTokenAddress, ERC20Abi, this.getProviderOnL2());
            const bridgeContract = getContract(this.contractAddressMap.Bridge, BridgeAbi, rpcSigner);
            const baseTokenContract = getContract(L2_BASE_TOKEN_ADDRESS, L2BaseTokenAbi, rpcSigner);
            const isBaseToken = withdrawTokenAddress === this.contractAddressMap.ZkCRO;
            if (isBaseToken) {
                return baseTokenContract.withdraw(to, {
                    value: amount.shl(ZKCRO_DECIMALS).quotient,
                });
            }
            const amountBigInt = await calculateAmountBigInt(amount, tokenContract);
            const tx = bridgeContract.withdraw(to, withdrawTokenAddress, amountBigInt);
            await executeTx(tx);
            return tx;
        };
        /**
         * When depositing a token on L1, the address of the result token on L2 is different.
         * It will be returned by this function.
         *
         * @param l1address address of a token to be deposited on L1
         * @returns corresponding L2 token
         */
        this.getDepositTokenL2Address = (l1address) => {
            const provider = getProvider(this.l2providerOptions, ChainLayer.L2);
            return provider.l2TokenAddress(l1address);
        };
        /**
         * When withdrawing a token on L2, the address of the result token on L1 is different.
         * It will be returned by this function.
         *
         * @param l1address address of a token to be withdrawed on L2
         * @returns corresponding L1 token
         */
        this.getWithdrawTokenL1Address = (l2address) => {
            const provider = getProvider(this.l2providerOptions, ChainLayer.L2);
            return provider.l1TokenAddress(l2address);
        };
        // it would be better to await l1Signer.isWithdrawalFinalized(parsedLog.transactionHash),
        // however the SDK has a bug in this function, hence we just estimate gas and if it fails
        // we assume that's because transaction was already finalized
        this.checkIsFinalized = async (l1Signer, parsedLog, finalizedBlockNumber) => {
            if (finalizedBlockNumber < parsedLog.blockNumber)
                return false;
            const { l1BatchNumber, l2MessageIndex, l2TxNumberInBlock, message, proof } = await l1Signer.finalizeWithdrawalParams(parsedLog.transactionHash, 0);
            const l1SharedBridge = IL1SharedBridge__factory.connect(await (await l1Signer.getL1BridgeContracts()).shared.getAddress(), getProvider(this.l1providerOptions, ChainLayer.L1));
            try {
                await l1SharedBridge.finalizeWithdrawal.estimateGas(
                // disabling ts warnings; this code is
                // copy/paste from zksync-ethers, the finalizeWithdrawal in zksync-ethers.
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (await this.getProviderOnL2().getNetwork()).chainId, 
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                l1BatchNumber, 
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                l2MessageIndex, 
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                l2TxNumberInBlock, message, proof, { from: l1Signer.address });
                // we can estimate gas, so we can call the function, assume that it's not yet finalized (else: error)
                return false;
            }
            catch (e) {
                // assume there's an error because this is finalized already
                return true;
            }
        };
        /**
         * Get an L1Signer object from a browser's rpc signer connected to L1.
         *
         * @param rpcSigner - browser's rpc signer for L1
         * @returns an L1Signer that's able to do deposits on L1 and also check the deposit status on L2
         */
        this.getL1Signer = (rpcSigner) => L1Signer.from(rpcSigner, 
        // read only provider for checking L2 (not L1, L1 is the rpcSigner param)
        this.getProviderOnL2());
        /**
         * Get a Provider object for the L2 network.
         *
         * @returns a Provider object for the L2 network with no signing capability
         */
        this.getProviderOnL2 = () => {
            if (!this.provider) {
                this.provider = new Provider(this.l2providerOptions.rpcUrl);
            }
            return this.provider;
        };
    }
}
const withdrawalEventABI = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: 'l2Sender',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'l1Receiver',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: 'l2Token',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: 'amount',
                type: 'uint256',
            },
        ],
        name: 'WithdrawalInitiated',
        type: 'event',
    },
];
const baseTokenWithdrawalEventABI = [
    {
        anonymous: false,
        inputs: [
            {
                indexed: true,
                internalType: 'address',
                name: '_l2Sender',
                type: 'address',
            },
            {
                indexed: true,
                internalType: 'address',
                name: '_l1Receiver',
                type: 'address',
            },
            {
                indexed: false,
                internalType: 'uint256',
                name: '_amount',
                type: 'uint256',
            },
        ],
        name: 'Withdrawal',
        type: 'event',
    },
];
